import { FrequentTimeZones } from '@airreading/common/time-zones';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod';

/** Use the user's preference when available, then the browser zone, then UTC. */
export function getTimeZone(preferredTimeZone: string | null | undefined): string {
  return preferredTimeZone || Temporal.Now.timeZoneId() || FrequentTimeZones.UTC;
}

export function getNowPlainDateISO(preferredTimeZone: string | null | undefined): Temporal.PlainDate {
  return Temporal.Now.plainDateISO(getTimeZone(preferredTimeZone));
}

/** Calendar widgets use native local dates, even when the browser cannot name its time zone. */
export function plainDateFromLocalDate(date: Date): Temporal.PlainDate {
  return new Temporal.PlainDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
}

export function localDateFromPlainDate(date: Temporal.PlainDate): Date {
  const localDate = new Date(0);
  localDate.setFullYear(date.year, date.month - 1, date.day);
  localDate.setHours(0, 0, 0, 0);
  return localDate;
}

export const timezoneZodSchema = z.object({ value: z.string(), label: z.string() });

export class TimeZoneOption {
  constructor(
    public label: string,
    public value: string,
    public standardAbbr: string | undefined,
  ) {}

  toString(): string {
    return this.value;
  }
}

export const AllTimeZones = [
  {
    label: '(GMT-08:00) Pacific Time (US & Canada)',
    value: 'US/Pacific',
    abbr: 'PDT',
    standardAbbr: 'PT',
  },
  {
    label: '(GMT-07:00) Mountain Time (US & Canada)',
    value: 'US/Mountain',
    abbr: 'MDT',
    standardAbbr: 'MT',
  },
  {
    label: '(GMT-06:00) Central Time (US & Canada)',
    value: 'US/Central',
    abbr: 'CDT',
    standardAbbr: 'CT',
  },
  {
    label: '(GMT-05:00) Eastern Time (US & Canada)',
    value: 'US/Eastern',
    abbr: 'EDT',
    standardAbbr: 'ET',
  },
  { label: '(GMT-07:00) Arizona', value: 'US/Arizona', abbr: 'UMST', standardAbbr: 'MT' },
  {
    label: '(GMT-05:00) Indiana (East)',
    value: 'US/East-Indiana',
    abbr: 'UEDT',
    standardAbbr: 'ET',
  },
  {
    label: '(GMT-10:00) Hawaii',
    value: 'Pacific/Honolulu',
    abbr: 'HST',
  },
  { label: '(GMT-09:00) Alaska', value: 'US/Alaska', abbr: 'AKDT' },
  {
    label: '(GMT-12:00) International Date Line West',
    value: 'Etc/GMT+12',
    abbr: 'DST',
  },
  {
    label: '(GMT-11:00) Midway Island, Samoa',
    value: 'Pacific/Midway',
    abbr: 'U',
  },
  {
    label: '(GMT-08:00) Tijuana, Baja California',
    value: 'America/Tijuana',
    abbr: 'PDT',
  },
  {
    label: '(GMT-07:00) Chihuahua, La Paz, Mazatlan',
    value: 'America/Chihuahua',
    abbr: 'MDT',
  },
  {
    label: '(GMT-06:00) Central America',
    value: 'America/Managua',
    abbr: 'CAST',
  },
  {
    label: '(GMT-06:00) Guadalajara, Mexico City, Monterrey',
    value: 'America/Mexico_City',
    abbr: 'CDT',
  },
  {
    label: '(GMT-06:00) Saskatchewan',
    value: 'Canada/Saskatchewan',
    abbr: 'CCST',
  },
  {
    label: '(GMT-05:00) Bogota, Lima, Quito, Rio Branco',
    value: 'America/Bogota',
    abbr: 'SPST',
  },
  {
    label: '(GMT-04:00) Atlantic Time (Canada)',
    value: 'Canada/Atlantic',
    abbr: 'ADT',
  },
  {
    label: '(GMT-04:00) Caracas, La Paz',
    value: 'America/Caracas',
    abbr: 'VST',
  },
  {
    label: '(GMT-04:00) Manaus',
    value: 'America/Manaus',
    abbr: 'SWST',
  },
  {
    label: '(GMT-04:00) Santiago',
    value: 'America/Santiago',
    abbr: 'PSST',
  },
  {
    label: '(GMT-03:30) Newfoundland',
    value: 'Canada/Newfoundland',
    abbr: 'NDT',
  },
  {
    label: '(GMT-03:00) Brasilia',
    value: 'America/Sao_Paulo',
    abbr: 'ESAST',
  },
  {
    label: '(GMT-03:00) Buenos Aires, Georgetown',
    value: 'America/Argentina/Buenos_Aires',
    abbr: 'AST',
  },
  {
    label: '(GMT-03:00) Greenland',
    value: 'America/Godthab',
    abbr: 'GDT',
  },
  {
    label: '(GMT-03:00) Montevideo',
    value: 'America/Montevideo',
    abbr: 'MST',
  },
  {
    label: '(GMT-02:00) Mid-Atlantic',
    value: 'America/Noronha',
    abbr: 'U',
  },
  {
    label: '(GMT-01:00) Cape Verde Is.',
    value: 'Atlantic/Cape_Verde',
    abbr: 'CVST',
  },
  {
    label: '(GMT-01:00) Azores',
    value: 'Atlantic/Azores',
    abbr: 'ADT',
  },
  {
    label: '(GMT+00:00) Casablanca, Monrovia, Reykjavik',
    value: 'Africa/Casablanca',
    abbr: 'MDT',
  },
  {
    label: '(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London',
    value: 'Europe/London',
    abbr: 'GMT',
  },
  {
    label: '(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna',
    value: 'Europe/Amsterdam',
    abbr: 'WEDT',
  },
  {
    label: '(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague',
    value: 'Europe/Belgrade',
    abbr: 'CEDT',
  },
  {
    label: '(GMT+01:00) Brussels, Copenhagen, Madrid, Paris',
    value: 'Europe/Brussels',
    abbr: 'RDT',
  },
  {
    label: '(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb',
    value: 'Europe/Sarajevo',
    abbr: 'CEDT',
  },
  {
    label: '(GMT+01:00) West Central Africa',
    value: 'Africa/Lagos',
    abbr: 'WCAST',
  },
  { label: '(GMT+02:00) Amman', value: 'Asia/Amman', abbr: 'JST' },
  {
    label: '(GMT+02:00) Athens, Bucharest, Istanbul',
    value: 'Europe/Athens',
    abbr: 'GDT',
  },
  { label: '(GMT+02:00) Beirut', value: 'Asia/Beirut', abbr: 'MEDT' },
  { label: '(GMT+02:00) Cairo', value: 'Africa/Cairo', abbr: 'EST' },
  {
    label: '(GMT+02:00) Harare, Pretoria',
    value: 'Africa/Harare',
    abbr: 'SAST',
  },
  {
    label: '(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius',
    value: 'Europe/Helsinki',
    abbr: 'EEDT',
  },
  {
    label: '(GMT+02:00) Jerusalem',
    value: 'Asia/Jerusalem',
    abbr: 'JDT',
  },
  { label: '(GMT+02:00) Minsk', value: 'Europe/Minsk', abbr: 'MSK' },
  {
    label: '(GMT+02:00) Windhoek',
    value: 'Africa/Windhoek',
    abbr: 'NST',
  },
  {
    label: '(GMT+03:00) Kuwait, Riyadh, Baghdad',
    value: 'Asia/Kuwait',
    abbr: 'AST',
  },
  {
    label: '(GMT+03:00) Moscow, St. Petersburg, Volgograd',
    value: 'Europe/Moscow',
    abbr: 'MSK',
  },
  {
    label: '(GMT+03:00) Nairobi',
    value: 'Africa/Nairobi',
    abbr: 'EAST',
  },
  { label: '(GMT+03:00) Tbilisi', value: 'Asia/Tbilisi', abbr: 'GET' },
  { label: '(GMT+03:30) Tehran', value: 'Asia/Tehran', abbr: 'IDT' },
  {
    label: '(GMT+04:00) Abu Dhabi, Muscat',
    value: 'Asia/Muscat',
    abbr: 'AST',
  },
  { label: '(GMT+04:00) Baku', value: 'Asia/Baku', abbr: 'ADT' },
  { label: '(GMT+04:00) Yerevan', value: 'Asia/Yerevan', abbr: 'CST' },
  { label: '(GMT+04:30) Kabul', value: 'Asia/Kabul', abbr: 'AST' },
  {
    label: '(GMT+05:00) Yekaterinburg',
    value: 'Asia/Yekaterinburg',
    abbr: 'YEKT',
  },
  {
    label: '(GMT+05:00) Islamabad, Karachi, Tashkent',
    value: 'Asia/Karachi',
    abbr: 'PKT',
  },
  {
    label: '(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi',
    value: 'Asia/Calcutta',
    abbr: 'IST',
  },
  {
    label: '(GMT+05:45) Kathmandu',
    value: 'Asia/Kathmandu',
    abbr: 'NST',
  },
  {
    label: '(GMT+06:00) Almaty, Novosibirsk',
    value: 'Asia/Almaty',
    abbr: 'CAST',
  },
  {
    label: '(GMT+06:00) Astana, Dhaka',
    value: 'Asia/Dhaka',
    abbr: 'BST',
  },
  {
    label: '(GMT+06:30) Yangon (Rangoon)',
    value: 'Asia/Rangoon',
    abbr: 'MST',
  },
  {
    label: '(GMT+07:00) Bangkok, Hanoi, Jakarta',
    value: 'Asia/Bangkok',
    abbr: 'SAST',
  },
  {
    label: '(GMT+07:00) Krasnoyarsk',
    value: 'Asia/Krasnoyarsk',
    abbr: 'NAST',
  },
  {
    label: '(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi',
    value: 'Asia/Hong_Kong',
    abbr: 'CST',
  },
  {
    label: '(GMT+08:00) Kuala Lumpur, Singapore',
    value: 'Asia/Kuala_Lumpur',
    abbr: 'MPST',
  },
  {
    label: '(GMT+08:00) Irkutsk, Ulaan Bataar',
    value: 'Asia/Irkutsk',
    abbr: 'NAEST',
  },
  {
    label: '(GMT+08:00) Perth',
    value: 'Australia/Perth',
    abbr: 'WAST',
  },
  { label: '(GMT+08:00) Taipei', value: 'Asia/Taipei', abbr: 'TST' },
  {
    label: '(GMT+09:00) Osaka, Sapporo, Tokyo',
    value: 'Asia/Tokyo',
    abbr: 'JST',
  },
  { label: '(GMT+09:00) Seoul', value: 'Asia/Seoul', abbr: 'KST' },
  { label: '(GMT+09:00) Yakutsk', value: 'Asia/Yakutsk', abbr: 'YST' },
  {
    label: '(GMT+09:30) Adelaide',
    value: 'Australia/Adelaide',
    abbr: 'CAST',
  },
  {
    label: '(GMT+09:30) Darwin',
    value: 'Australia/Darwin',
    abbr: 'ACST',
  },
  {
    label: '(GMT+10:00) Brisbane',
    value: 'Australia/Brisbane',
    abbr: 'EAST',
  },
  {
    label: '(GMT+10:00) Canberra, Melbourne, Sydney',
    value: 'Australia/Canberra',
    abbr: 'AEST',
  },
  {
    label: '(GMT+10:00) Hobart',
    value: 'Australia/Hobart',
    abbr: 'TST',
  },
  {
    label: '(GMT+10:00) Guam, Port Moresby',
    value: 'Pacific/Guam',
    abbr: 'WPST',
  },
  {
    label: '(GMT+10:00) Vladivostok',
    value: 'Asia/Vladivostok',
    abbr: 'VST',
  },
  {
    label: '(GMT+11:00) Magadan, Solomon Is., New Caledonia',
    value: 'Asia/Magadan',
    abbr: 'MST',
  },
  {
    label: '(GMT+12:00) Auckland, Wellington',
    value: 'Pacific/Auckland',
    abbr: 'NZST',
  },
  {
    label: '(GMT+12:00) Fiji, Kamchatka, Marshall Is.',
    value: 'Pacific/Fiji',
    abbr: 'FST',
  },
  {
    label: "(GMT+13:00) Nuku'alofa",
    value: 'Pacific/Tongatapu',
    abbr: 'TST',
  },
].map(({ label, value, standardAbbr }) => new TimeZoneOption(label, value, standardAbbr));

/**
 * A helper for the time zone selector form controls. Please do not use this
 * function to determine if a string is a valid time zone.
 */
export const findTimeZoneByValue = (tz?: string | null): TimeZoneOption | undefined =>
  AllTimeZones.find(({ value }) => value === tz);

/**
 * Returns a typeahead fetcher for timezone options.
 * - No results are shown until the user types.
 * - Filters on label, value, or standard abbreviation.
 */
export const getTimezoneTypeahead = (input: string) => {
  const query = (input || '').trim().toLowerCase();
  if (!query) return [] as { label: string; value: string }[];

  return AllTimeZones.filter(({ label, value, standardAbbr }) => {
    const ll = label.toLowerCase();
    const vv = value.toLowerCase();
    const aa = standardAbbr ? standardAbbr.toLowerCase() : '';
    return ll.includes(query) || vv.includes(query) || aa.includes(query);
  }).map(({ label, value }) => ({ label, value }));
};
